/**
 * Contact view component.
 * @module components/View/ContactView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Confirm,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent, searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${year}-${month}-${day} ${hours}:${minutesStr} ${ampm}`;
};

/**
 * Contact view component class.
 * @function ContactView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ContactView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  useEffect(() => {
    dispatch(
      searchContent(
        '',
        { portal_type: 'Order', contact: content.id },
        'contact-orders',
      ),
    );
  }, [dispatch, content.id]);

  const orders = useSelector(
    (state) => state.search.subrequests?.['contact-orders']?.items || [],
  );

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">Contact</div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete contact"
        content={`Are you sure you want to delete "${content.title}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="contact">
          <h1 className="content-title">{content.title}</h1>
          <table className="tables-side-by-side">
            <tbody>
              <tr>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="6"
                          textAlign="center"
                          className="general sub rotated"
                        >
                          <span className="rotate-label">General</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="5">First Name</TableHeaderCell>
                        <TableCell width="7">{content.first_name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Middle Name</TableHeaderCell>
                        <TableCell>{content.middle_name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Last Name</TableHeaderCell>
                        <TableCell>{content.last_name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Company</TableHeaderCell>
                        <TableCell>{content.company}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableCell>{content.email}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Phone</TableHeaderCell>
                        <TableCell>{content.phone}</TableCell>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </td>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="6"
                          textAlign="center"
                          className="preparation sub rotated"
                        >
                          <span className="rotate-label">Address</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="5">Street</TableHeaderCell>
                        <TableCell width="7">{content.street}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>City</TableHeaderCell>
                        <TableCell>{content.city}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>State</TableHeaderCell>
                        <TableCell>{content.state}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Postal Code</TableHeaderCell>
                        <TableCell>{content.postal_code}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Country</TableHeaderCell>
                        <TableCell>{content.country}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Notes</TableHeaderCell>
                        <TableCell>{content.notes}</TableCell>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </td>
              </tr>
            </tbody>
          </table>
          <Table celled selectable>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  rowSpan={orders.length === 0 ? 2 : orders.length + 1}
                  textAlign="center"
                  className="machining sub rotated"
                >
                  <span className="rotate-label">Orders</span>
                </TableHeaderCell>
                <TableHeaderCell width="4">Order #</TableHeaderCell>
                <TableHeaderCell width="4">Date</TableHeaderCell>
                <TableHeaderCell width="4">Channel</TableHeaderCell>
              </TableRow>
              {orders.map((item) => (
                <TableRow
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{formatDate(item.date)}</TableCell>
                  <TableCell>{item.channel}</TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan="3" textAlign="center">
                    No orders yet.
                  </TableCell>
                </TableRow>
              )}
            </TableHeader>
          </Table>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ContactView.propTypes = {
  content: PropTypes.shape({
    first_name: PropTypes.string,
    middle_name: PropTypes.string,
    last_name: PropTypes.string,
    company: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postal_code: PropTypes.string,
    country: PropTypes.string,
    notes: PropTypes.string,
  }).isRequired,
};

export default ContactView;
